import { useCallback, useEffect, useState } from 'react';
import type { MouseEvent } from 'react';
import { useApolloClient } from '@apollo/client';
import { useFormState } from 'react-hook-form';
import { isEqual } from 'lodash-es';
import { ModalDialog } from '../../modal/layout/ModalDialog';
import { ModalFooter } from '../../modal/layout/ModalFooter';
import { ModalHeader } from '../../modal/layout/ModalHeader';
import { useProtectedClose } from '../../modal/hooks/useProtectedClose';
import {
    NOTIFICATION_VARIANT,
    NotificationsContainer,
    useNotifications,
} from '../../notifications/Notifications';
import Form from '../../reactForm/Form';
import { Layout, PlainGroupContainer } from '../../reactForm/Layout';
import { useDeepCompareMemoize } from '../../../hooks/useDeepCompareMemoize';
import { isRecord } from '../../../utils/typeGuards';
import { requireReportElementClass } from './elements/elementSettingsFormSchema';
import type {
    ElementDefinition,
    ElementSettings,
    RenderContext,
    ElementSettingsFormSchema,
    RenderContextKind,
    RenderRuntimeDataServices,
} from './elements/types';
import { toErrorMessage } from './forms/errorMessage';
import { useElementSettingsFormController } from './forms/useElementSettingsFormController';

interface ElementSettingsModalProps {
    definition: ElementDefinition;
    contextKind: RenderContextKind;
    context: RenderContext;
    runtimeDataServices: RenderRuntimeDataServices;
    onClose: () => void;
    onSave: (settings: Record<string, unknown>) => void;
}

interface ResolvedElementSettingsContentProps {
    definition: ElementDefinition;
    settingsDraft: ElementSettings;
    resolvedSettingsFormSchema: ElementSettingsFormSchema;
    settingsFormSchemaDependencies: readonly string[];
    error: string;
    onClose: () => void;
    onSettingsFormSchemaDependencyChange: (settingsDraft: Record<string, unknown>) => void;
    onSave: (settings: Record<string, unknown>) => void;
    onBindingChange: (formValues: ElementSettings) => void;
    setError: (value: string) => void;
    onFormDirtyChange: (isDirty: boolean) => void;
}

interface ResolvedSettingsFormSchema {
    dependencyKey: string;
    schema: ElementSettingsFormSchema;
}

const BINDING_ARG_FIELD_NAME = 'binding.arg';

function readBindingId(settings: ElementSettings): unknown {
    return isRecord(settings.binding) ? settings.binding.bindingId : undefined;
}

function ResolvedElementSettingsContent({
    definition,
    settingsDraft,
    resolvedSettingsFormSchema,
    settingsFormSchemaDependencies,
    error,
    onClose,
    onSettingsFormSchemaDependencyChange,
    onSave,
    onBindingChange,
    setError,
    onFormDirtyChange,
}: ResolvedElementSettingsContentProps): JSX.Element {
    const settingsFormController = useElementSettingsFormController({
        elementType: definition.type,
        currentSettings: definition.settings,
        // Saved values underneath: a field the previous schema hid has no live value
        // and would otherwise be re-seeded from its schema default.
        initialFormValues: { ...definition.settings, ...settingsDraft },
        settingsFormSchema: resolvedSettingsFormSchema,
        onSubmitSettings(nextSettings) {
            onSave(nextSettings);
        },
        onSubmitError(submitError) {
            setError(toErrorMessage(submitError));
        },
    });

    const { setValue, watch } = settingsFormController.rhfProps;
    const { isDirty } = useFormState({ control: settingsFormController.rhfProps.control });

    useEffect(() => {
        onFormDirtyChange(isDirty);
    }, [isDirty, onFormDirtyChange]);

    useEffect(() => {
        const subscription = watch((values, { name }) => {
            if (name !== 'binding.bindingId' || !isRecord(values)) {
                return;
            }

            setValue(BINDING_ARG_FIELD_NAME, '');
            // The draft is what the schema gets resolved from, so it is built the same way
            // the dependency watcher below builds it. Raw form values would carry the empty
            // defaults of untouched fields, and an element that resolves its schema from one
            // of them, as an image does from `sizing`, reads that as a real change and drops
            // the schema this form is still using.
            onBindingChange(settingsFormController.createSettingsDraft(values, name));
        });

        return () => subscription.unsubscribe();
    }, [onBindingChange, setValue, settingsFormController, watch]);

    useEffect(() => {
        if (settingsFormSchemaDependencies.length === 0) {
            return undefined;
        }

        const subscription = watch((values, { name }) => {
            if (!name || !settingsFormSchemaDependencies.includes(name)) {
                return;
            }

            onSettingsFormSchemaDependencyChange(
                settingsFormController.createSettingsDraft(values, name)
            );
        });

        return () => subscription.unsubscribe();
    }, [
        onSettingsFormSchemaDependencyChange,
        settingsFormController,
        settingsFormSchemaDependencies,
        watch,
    ]);

    function handleControllerSave(event: MouseEvent<HTMLButtonElement>) {
        setError('');
        settingsFormController.triggerSubmit(event).catch(() => undefined);
    }

    return (
        <>
            <div className="space-y-4 px-6">
                <Form formController={settingsFormController} showSaveButton={false}>
                    <Layout
                        formController={settingsFormController}
                        GroupContainer={PlainGroupContainer}
                    />
                </Form>
                {error ? <div className="text-xs text-red-600">{error}</div> : null}
            </div>
            <ModalFooter onClose={onClose} onSave={handleControllerSave} saveDisabled={false} />
        </>
    );
}

export function ElementSettingsModal({
    definition,
    contextKind,
    context,
    runtimeDataServices,
    onClose,
    onSave,
}: ElementSettingsModalProps): JSX.Element {
    const elementClass = requireReportElementClass(definition.type);
    const client = useApolloClient();

    const modalTitle = interpolate(
        gettext('ELEMENT_SETTINGS_TITLE'),
        { label: elementClass.insertion.label },
        true
    );
    const [error, setError] = useState('');
    // `useForm` binds its form control once per mount, so a resolved schema only
    // takes effect for validation and submit when the form is mounted again.
    const [schemaRevision, setSchemaRevision] = useState(0);
    const savedSettings = useDeepCompareMemoize(definition.settings);
    const [settingsDraft, setSettingsDraft] = useState<ElementSettings>(savedSettings);
    const settingsForSchema = useDeepCompareMemoize({ ...savedSettings, ...settingsDraft });
    const settingsFormSchemaDependencies = elementClass.settingsFormSchemaDependencies ?? [];
    const settingsFormSchemaDependencyKey = JSON.stringify(
        settingsFormSchemaDependencies.map((name) => [name, settingsDraft[name]])
    );
    const [resolvedSettingsFormSchema, setResolvedSettingsFormSchema] =
        useState<ResolvedSettingsFormSchema | null>(null);
    // react-hook-form retains the first formControl passed to a mounted Form.
    // Only render a schema matching the current draft, then remount the form when its
    // dependencies change so stale fields are neither displayed nor submitted.
    const activeSettingsFormSchema =
        resolvedSettingsFormSchema?.dependencyKey === settingsFormSchemaDependencyKey
            ? resolvedSettingsFormSchema.schema
            : null;

    const [isFormDirty, setIsFormDirty] = useState(false);
    const { addNotification } = useNotifications();
    const { attemptClose, isShaking, showWarning } = useProtectedClose({
        isOpen: true,
        // Schema changes remount the form with the draft as its new defaults.
        shouldWarn: isFormDirty || !isEqual(settingsDraft, savedSettings),
        onClose,
    });

    useEffect(() => {
        if (!showWarning) return undefined;

        return addNotification({
            message: gettext('UNSAVED_CHANGES_CLOSE_AGAIN'),
            variant: NOTIFICATION_VARIANT.WARNING,
        }).remove;
    }, [addNotification, showWarning]);

    useEffect(() => {
        setSettingsDraft(savedSettings);
        setIsFormDirty(false);
        setResolvedSettingsFormSchema(null);
        setError('');
    }, [savedSettings]);

    useEffect(() => {
        let isActive = true;

        void elementClass
            .resolveSettingsFormSchema({
                contextKind,
                context,
                client,
                runtimeDataServices,
                currentSettings: settingsForSchema,
            })
            .then((settingsFormSchema) => {
                if (!isActive) {
                    return;
                }

                setResolvedSettingsFormSchema({
                    dependencyKey: settingsFormSchemaDependencyKey,
                    schema: settingsFormSchema,
                });
                setSchemaRevision((revision) => revision + 1);
            })
            .catch((resolveError) => {
                if (!isActive) {
                    return;
                }

                setResolvedSettingsFormSchema(null);
                setError(toErrorMessage(resolveError));
            });

        return () => {
            isActive = false;
        };
    }, [
        contextKind,
        context,
        settingsForSchema,
        settingsFormSchemaDependencyKey,
        elementClass,
        runtimeDataServices,
        client,
    ]);

    const handleBindingChange = useCallback((nextFormValues: ElementSettings) => {
        setSettingsDraft((previousDraft) => {
            if (readBindingId(previousDraft) === readBindingId(nextFormValues)) {
                return previousDraft;
            }

            const binding = isRecord(nextFormValues.binding) ? nextFormValues.binding : {};
            return { ...nextFormValues, binding: { ...binding, arg: '' } };
        });
        setError('');
    }, []);

    return (
        <ModalDialog
            open={true}
            onClose={attemptClose}
            closeOnClickOutside={true}
            closeOnEsc={true}
            shake={isShaking}
        >
            <div className="relative flex flex-col py-6">
                <ModalHeader title={modalTitle} onClose={attemptClose} />
                <div className="px-6">
                    <NotificationsContainer />
                </div>
                {activeSettingsFormSchema ? (
                    <ResolvedElementSettingsContent
                        key={schemaRevision}
                        definition={definition}
                        settingsDraft={settingsDraft}
                        resolvedSettingsFormSchema={activeSettingsFormSchema}
                        settingsFormSchemaDependencies={settingsFormSchemaDependencies}
                        error={error}
                        onClose={attemptClose}
                        onFormDirtyChange={setIsFormDirty}
                        onSettingsFormSchemaDependencyChange={setSettingsDraft}
                        onSave={onSave}
                        onBindingChange={handleBindingChange}
                        setError={setError}
                    />
                ) : (
                    <>
                        <div className="space-y-4 px-6">
                            <div className="text-xs text-gray-600">{gettext('LOADING')}</div>
                            {error ? <div className="text-xs text-red-600">{error}</div> : null}
                        </div>
                        <ModalFooter
                            onClose={attemptClose}
                            onSave={(_event) => undefined}
                            saveDisabled={true}
                        />
                    </>
                )}
            </div>
        </ModalDialog>
    );
}
